import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Briefcase, Download, FileSignature, History, Loader2, Mail, RefreshCw, Sparkles, TrendingUp,
  TriangleAlert, Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { blobErrorDetail } from '../../lib/blobError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reports/commercial-analysis`;

const STATUS_STYLE = {
  vert: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  orange: 'bg-amber-100 text-amber-700 border-amber-200',
  rouge: 'bg-red-100 text-red-700 border-red-200',
};
const LEVEL_STYLE = {
  critique: 'text-red-700', eleve: 'text-orange-700', moyen: 'text-amber-700', faible: 'text-emerald-700',
};
const SEGMENT_LABELS = {
  new: 'Nouveaux', reactivated: 'Réactivés', growing: 'En croissance', stable: 'Stables',
  declining: 'En baisse', dormant: 'Dormants', lost: 'Perdus', at_risk: 'À risque',
  strategic: 'Stratégiques', non_mesure: 'Non mesurés',
};
const BILLING_LABELS = {
  per_unit: "À l'unité", per_tonnage: 'Au tonnage', monthly_fixed: 'Forfait mensuel',
  fleet_location: 'Location de flotte', per_km: 'Au kilomètre',
};
const expiry = (daysLeft, autoRenew) => {
  if (daysLeft === null || daysLeft === undefined) return 'Non mesuré';
  if (daysLeft < 0) return `échu (${Math.abs(daysLeft)} j)`;
  if (daysLeft <= 90) return `dans ${daysLeft} j${autoRenew ? '' : ' · sans reconduction'}`;
  return `dans ${daysLeft} j`;
};
const margin = (row) => {
  if (row?.marge_pct === null || row?.marge_pct === undefined) return 'Non mesurable';
  const suffix = row.marge_statut === 'estimated' ? ' (est.)' : '';
  const comp = row.completude_couts_pct;
  return `${row.marge_pct} %${suffix}${comp !== null && comp !== undefined ? ` · coûts ${comp} %` : ''}`;
};

const fcfa = (v) => (v === null || v === undefined || v === ''
  ? 'Non mesuré'
  : `${Number(v).toLocaleString('fr-FR')} FCFA`);
const nd = (v, suffix = '') => (v === null || v === undefined || v === '' ? 'Non mesuré' : `${v}${suffix}`);
// 0 FCFA n'est affiché que si c'est un vrai zéro mesuré (jamais pour une donnée absente)
const ca = (v, ops) => {
  if (v === null || v === undefined || v === '') return 'Non mesuré';
  if (!Number(v)) return ops ? 'Non facturé' : '—';
  return fcfa(v);
};

const download = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

const Kpi = ({ label, value, hint, testid }) => (
  <div className="border rounded-lg p-3 min-w-0" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-base font-bold text-gray-900 truncate">{value}</p>
    {hint && <p className="text-[11px] text-gray-500 truncate">{hint}</p>}
  </div>
);

/** Analyse commerciale IA (portefeuille, CA à risque, commerciaux) — direction uniquement. */
export const CommercialAiAnalysisCard = ({ periodParams, periodLabel, canConfigure, onSelectPeriod }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [archiveDownloading, setArchiveDownloading] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [recipientsText, setRecipientsText] = useState('');
  const [history, setHistory] = useState([]);

  const periodReady = Boolean(
    (periodParams?.year && periodParams?.month) || (periodParams?.start_date && periodParams?.end_date)
  );
  const paramsKey = JSON.stringify(periodParams || {});

  const fetchLatest = useCallback(async () => {
    if (!periodReady) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/latest`, { params: periodParams });
      setReport(data?.report ? data : null);
    } catch (err) {
      setReport(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, periodReady]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  useEffect(() => {
    axios.get(`${API}/history`, { params: { limit: 12 } })
      .then(({ data }) => setHistory(data?.items || []))
      .catch(() => setHistory([]));
  }, [report]);

  useEffect(() => {
    if (!canConfigure) return;
    axios.get(`${API}/schedule`)
      .then(({ data }) => {
        setSchedule(data);
        setRecipientsText((data?.recipients || []).join(', '));
      })
      .catch(() => setSchedule(null));
  }, [canConfigure]);

  const handleGenerate = async () => {
    if (!periodReady) return toast.error('Sélectionnez une période pour lancer l\'analyse');
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/generate`, null, { params: periodParams });
      setReport(data);
      toast.success('Rapport commercial généré');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analyse IA indisponible');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!periodReady) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/download/pdf`, { params: periodParams, responseType: 'blob' });
      download(res.data, `Rapport_Commercial_${(periodLabel || '').replace(/\s+/g, '_')}.pdf`);
      toast.success('Rapport PDF téléchargé');
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Téléchargement impossible'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadArchived = async (entry) => {
    setArchiveDownloading(entry.id);
    try {
      const res = await axios.get(`${API}/download/pdf`, {
        params: { report_id: entry.id }, responseType: 'blob',
      });
      download(res.data, `Rapport_Commercial_${(entry.periodLabel || '').replace(/\s+/g, '_')}.pdf`);
      toast.success(`Rapport ${entry.periodLabel} téléchargé`);
    } catch (err) {
      toast.error(await blobErrorDetail(err, 'Téléchargement impossible'));
    } finally {
      setArchiveDownloading(null);
    }
  };

  const saveSchedule = async (patch) => {
    setSavingSchedule(true);
    try {
      const payload = {
        ...schedule,
        ...patch,
        recipients: (patch?.recipients ?? recipientsText)
          .toString().split(/[,;\s]+/).map(s => s.trim()).filter(Boolean),
      };
      const { data } = await axios.put(`${API}/schedule`, payload);
      setSchedule(data);
      toast.success('Envoi automatique enregistré');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await axios.post(`${API}/send-now`, null, {
        params: { year: periodParams?.year, month: periodParams?.month },
      });
      toast.success(`Envoyé à ${(data.recipients_ok || []).length} destinataire(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Envoi impossible');
    } finally {
      setSendingNow(false);
    }
  };

  const r = report?.report;
  const ex = report?.metrics?.executive_summary || {};
  const segments = report?.metrics?.portefeuille?.repartition || [];
  const rar = report?.metrics?.revenue_at_risk || {};
  const commerciaux = report?.metrics?.commerciaux || [];
  const contrats = report?.metrics?.contrats || {};

  return (
    <Card className="mt-6 border-violet-200" data-testid="commercial-ai-card">
      <CardHeader className="bg-gradient-to-r from-violet-50 to-fuchsia-50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-violet-100 shrink-0">
              <Briefcase className="w-5 h-5 text-violet-700" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-lg">Analyse commerciale IA — rapport commercial</CardTitle>
              <CardDescription>
                Rapport de direction commerciale : d'où vient la croissance ou la baisse, portefeuille
                segmenté (nouveaux, en croissance, en baisse, dormants, perdus, réactivés),
                <strong> Revenue at Risk</strong> pondéré et documenté, croisement Customer Success
                (risque × valeur × évolution), performance par commercial
                (<em>acquire → develop → retain → reactivate</em>), pipeline, contrats et actions
                prioritaires. Période analysée : <strong>{periodLabel || '—'}</strong>.
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline" onClick={handleGenerate} disabled={generating || !periodReady}
              data-testid="generate-commercial-analysis"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {r ? 'Relancer l\'analyse' : 'Générer l\'analyse'}
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700" onClick={handleDownloadPdf}
              disabled={downloading || !periodReady} data-testid="download-commercial-analysis-pdf"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Rapport PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-5 space-y-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" /> Chargement du dernier rapport commercial…
          </div>
        )}

        {!loading && !r && (
          <p className="text-sm text-gray-500" data-testid="commercial-analysis-empty">
            Aucun rapport commercial enregistré pour {periodLabel || 'cette période'}.
            Cliquez sur « Générer l'analyse » — le rapport sera ensuite téléchargeable en PDF avec
            ses graphiques (CA spot vs contrats, clients actifs, segments, top clients, CA à risque).
          </p>
        )}

        {r && (
          <div className="space-y-5" data-testid="commercial-analysis-content">
            <div className="rounded-lg bg-violet-50/60 border border-violet-100 p-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 mb-1">
                Executive summary · {report.periodLabel} · {report.aiProvider}/{report.aiModel}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{r.synthese}</p>
            </div>

            <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="commercial-analysis-kpis">
              <Kpi label="CA HT période" value={fcfa(ex.ca_ht_periode)}
                   hint={`${nd(ex.variation_ca_pct, ' %')} vs préc.`} testid="kpi-ca-periode" />
              <Kpi label="Opérations" value={nd(ex.operations_periode)}
                   hint={`${nd(ex.variation_operations_pct, ' %')} vs préc.`} testid="kpi-operations" />
              <Kpi label="Clients actifs" value={nd(ex.clients_actifs_periode)}
                   hint={`sur ${nd(ex.clients_portefeuille)} au portefeuille`} testid="kpi-clients-actifs" />
              <Kpi label="Nouveaux / réactivés"
                   value={`${nd(ex.nouveaux_clients)} / ${nd(ex.clients_reactives)}`}
                   hint={`${nd(ex.clients_perdus)} perdu(s)`} testid="kpi-acquisition" />
              <Kpi label="CA moyen / client" value={fcfa(ex.ca_moyen_par_client_actif)}
                   hint={`panier ${fcfa(ex.panier_moyen_operation)}`} testid="kpi-ca-client" />
              <Kpi label="Revenue at Risk" value={fcfa(ex.revenue_at_risk_fcfa)}
                   hint={`${nd(ex.revenue_at_risk_part_ca_12m_pct, ' %')} du CA 12 mois`}
                   testid="kpi-revenue-at-risk" />
            </div>

            {r.dashboard?.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2" data-testid="commercial-analysis-dashboard">
                {r.dashboard.map((d, i) => (
                  <div key={i} className="border rounded-lg p-3 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-gray-600 truncate">{d.indicateur}</p>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[d.statut] || ''}`}>
                        {d.statut}
                      </Badge>
                    </div>
                    <p className="text-base font-bold text-gray-900">{d.valeur}</p>
                    <p className="text-[11px] text-gray-500">{d.variation} · {d.lecture}</p>
                  </div>
                ))}
              </div>
            )}

            {segments.length > 0 && (
              <div data-testid="commercial-analysis-segments">
                <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4 text-violet-700" /> Portefeuille par segment
                </p>
                <div className="flex flex-wrap gap-2">
                  {segments.map((s, i) => (
                    <div key={i} className="border rounded-lg px-3 py-2 min-w-0">
                      <p className="text-[11px] text-gray-500">{SEGMENT_LABELS[s.segment] || s.segment}</p>
                      <p className="text-sm font-bold text-gray-900">{s.clients} client(s)</p>
                      <p className="text-[11px] text-gray-500">{ca(s.ca_12m, s.clients)} sur 12 mois</p>
                    </div>
                  ))}
                </div>
                {r.portefeuille?.analyse && (
                  <p className="text-xs text-gray-600 mt-2">{r.portefeuille.analyse}</p>
                )}
              </div>
            )}

            {(rar.detail || []).length > 0 && (
              <div data-testid="commercial-analysis-rar">
                <p className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-red-700" /> Revenue at Risk — {fcfa(rar.total_fcfa)}
                </p>
                <p className="text-[11px] text-gray-500 mb-2">{rar.formule}</p>
                <div className="space-y-1.5">
                  {rar.detail.slice(0, 6).map((x, i) => (
                    <div key={i} className="border rounded-lg p-2.5 flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{x.societe}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {SEGMENT_LABELS[x.segment] || x.segment}
                      </Badge>
                      <span className="text-xs text-gray-600 font-mono">{fcfa(x.revenue_at_risk)}</span>
                      <span className="text-[11px] text-gray-500">
                        · {fcfa(x.ca_12m)} × {x.ponderation} · {x.commercial || 'Non affecté'}
                      </span>
                      {x.signaux?.length > 0 && (
                        <span className="text-[11px] text-gray-500 w-full">{x.signaux.join(' · ')}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {commerciaux.length > 0 && (
              <div data-testid="commercial-analysis-commercials">
                <p className="text-sm font-semibold text-gray-800 mb-2">Performance par commercial</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-gray-500">
                      <tr className="border-b">
                        <th className="py-2 pr-3 text-left font-medium">Commercial</th>
                        <th className="px-3 py-2 text-right font-medium">CA période</th>
                        <th className="px-3 py-2 text-right font-medium">Atteinte</th>
                        <th className="px-3 py-2 text-right font-medium">Portefeuille</th>
                        <th className="px-3 py-2 text-right font-medium">Nouveaux</th>
                        <th className="px-3 py-2 text-right font-medium">Réactivés</th>
                        <th className="px-3 py-2 text-right font-medium">À retenir</th>
                        <th className="px-3 py-2 text-right font-medium">CA à risque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commerciaux.map((c, i) => (
                        <tr key={i} className="border-b last:border-0"
                            data-testid={`commercial-row-${i}`}>
                          <td className="py-2 pr-3 font-medium text-gray-900">{c.commercial}</td>
                          <td className="px-3 py-2 text-right font-mono">{ca(c.ca_periode, c.operations_periode)}</td>
                          <td className="px-3 py-2 text-right font-mono">{nd(c.taux_atteinte_pct, ' %')}</td>
                          <td className="px-3 py-2 text-right font-mono">{c.portefeuille}</td>
                          <td className="px-3 py-2 text-right font-mono">{c.nouveaux_clients}</td>
                          <td className="px-3 py-2 text-right font-mono">{c.clients_reactives}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {c.clients_a_risque + c.clients_dormants}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{ca(c.revenue_at_risk, c.portefeuille)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {r.contrats?.analyse && (
              <div data-testid="commercial-analysis-contracts">
                <p className="text-sm font-semibold text-gray-800 mb-1 flex items-center gap-2">
                  <FileSignature className="w-4 h-4 text-violet-700" /> Contrats & revenus récurrents
                </p>
                <p className="text-xs text-gray-600 mb-2">{r.contrats.analyse}</p>
                {contrats.detail_par_contrat?.length > 0 && (
                  <>
                    <p className="text-[11px] text-gray-500 mb-2">
                      {contrats.contrats_analyses} contrat(s) analysé(s) ·
                      {' '}{contrats.contrats_en_cours} en cours ·
                      {' '}{contrats.contrats_expires} échu(s) ·
                      {' '}{contrats.contrats_expirant_bientot} expirant sous
                      {' '}{contrats.seuil_expiration_jours} j ·
                      {' '}{contrats.contrats_brouillon_exclus} brouillon(s) exclu(s) · revenu
                      récurrent moyen {ca(contrats.revenu_recurrent_mensuel_moyen, 1)}/mois
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="text-gray-500">
                          <tr className="border-b">
                            <th className="py-2 pr-3 text-left font-medium">Contrat</th>
                            <th className="px-3 py-2 text-left font-medium">Client</th>
                            <th className="px-3 py-2 text-left font-medium">Facturation</th>
                            <th className="px-3 py-2 text-right font-medium">Échéance</th>
                            <th className="px-3 py-2 text-right font-medium">CA période</th>
                            <th className="px-3 py-2 text-right font-medium">CA 12 mois</th>
                            <th className="px-3 py-2 text-right font-medium">Mois non facturés</th>
                            <th className="px-3 py-2 text-right font-medium">Transferts</th>
                            <th className="px-3 py-2 text-right font-medium">Var. 6 mois</th>
                            <th className="px-3 py-2 text-right font-medium">Marge</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contrats.detail_par_contrat.map((x, i) => (
                            <tr key={i} className="border-b last:border-0"
                                data-testid={`contract-row-${i}`}>
                              <td className="py-2 pr-3 font-medium text-gray-900">{x.contrat}</td>
                              <td className="px-3 py-2 text-gray-700">{x.client}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {BILLING_LABELS[x.mode_facturation] || x.mode_facturation || '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {expiry(x.jours_restants, x.renouvellement_auto)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {ca(x.ca_ht_periode, x.factures_periode)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {ca(x.ca_ht_12m, x.factures_12m)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">
                                {x.mois_non_factures?.length || '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">{x.transferts_periode}</td>
                              <td className="px-3 py-2 text-right font-mono">
                                {nd(x.variation_transferts_6m_pct, ' %')}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">{margin(x)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {contrats.signaux?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {contrats.signaux.slice(0, 6).map((s, i) => (
                      <li key={i} className="text-[11px] text-gray-600">
                        <strong>{s.contrat}</strong> ({s.client}) — {s.signal} → {s.action}
                      </li>
                    ))}
                  </ul>
                )}
                {contrats.opportunites_contractualisation?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[11px] font-semibold text-gray-700">
                      Clients spot réguliers sans contrat
                    </p>
                    <ul className="space-y-1">
                      {contrats.opportunites_contractualisation.slice(0, 5).map((o, i) => (
                        <li key={i} className="text-[11px] text-gray-600">
                          <strong>{o.societe}</strong> — {o.pourquoi} ({ca(o.ca_12m, o.operations_12m)} sur 12 mois)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {r.actions_prioritaires?.length > 0 && (
              <div data-testid="commercial-analysis-actions">
                <p className="text-sm font-semibold text-gray-800 mb-2">Actions commerciales prioritaires</p>
                <div className="space-y-2">
                  {r.actions_prioritaires.map((a, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-violet-600 text-[10px]">P{a.priorite}</Badge>
                        <p className="text-sm font-semibold text-gray-900 truncate">{a.societe}</p>
                        {a.commercial && (
                          <span className="text-[11px] text-gray-500">· {a.commercial}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{a.signal}</p>
                      <p className="text-[11px] text-gray-500">Impact : {a.impact}</p>
                      <p className="text-sm text-gray-800 mt-1">→ {a.action}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {r.risques?.length > 0 && (
              <div data-testid="commercial-analysis-risks">
                <p className="text-sm font-semibold text-gray-800 mb-2">Risques commerciaux</p>
                <ul className="space-y-1.5">
                  {r.risques.map((x, i) => (
                    <li key={i} className="text-xs text-gray-700 flex gap-2">
                      <TriangleAlert className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${LEVEL_STYLE[x.niveau] || ''}`} />
                      <span><strong>{x.titre}</strong> — {x.impact}. {x.mitigation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {r.opportunites?.length > 0 && (
              <div data-testid="commercial-analysis-opportunities">
                <p className="text-sm font-semibold text-gray-800 mb-2">Opportunités commerciales</p>
                <ul className="space-y-1.5">
                  {r.opportunites.map((x, i) => (
                    <li key={i} className="text-xs text-gray-700">
                      <strong>{x.titre}</strong> — gain estimé {x.gain}. {x.comment}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {r.qualite_donnees?.length > 0 && (
              <p className="text-[11px] text-gray-500" data-testid="commercial-analysis-quality">
                Fiabilité des données : {r.qualite_donnees.join(' · ')}
              </p>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="border-t pt-4" data-testid="commercial-analysis-history">
            <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <History className="w-4 h-4 text-violet-700" /> Historique des rapports commerciaux
            </p>
            <div className="flex flex-wrap gap-2">
              {history.map((h) => {
                const d = new Date(h.periodStart);
                const selectable = onSelectPeriod && !Number.isNaN(d.getTime());
                return (
                  <div key={h.id} className="flex items-stretch border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={selectable
                        ? () => onSelectPeriod({ year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 })
                        : undefined}
                      disabled={!selectable}
                      className={`px-3 py-2 min-w-0 text-left transition-colors ${
                        selectable ? 'hover:bg-violet-50/50 cursor-pointer' : ''}`}
                      data-testid={`commercial-history-${h.id}`}
                    >
                      <p className="text-xs font-semibold text-gray-900">{h.periodLabel}</p>
                      <p className="text-[11px] text-gray-500">
                        {fcfa(h.metrics?.executive_summary?.ca_ht_periode)} ·
                        {' '}{(h.generatedAt || '').slice(0, 10)}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownloadArchived(h)}
                      disabled={archiveDownloading === h.id}
                      title="Télécharger ce rapport archivé (PDF)"
                      className="px-2 border-l text-violet-700 hover:bg-violet-50 disabled:opacity-50"
                      data-testid={`commercial-history-download-${h.id}`}
                    >
                      {archiveDownloading === h.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Download className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {canConfigure && schedule && (
          <div className="border-t pt-4 space-y-3" data-testid="commercial-analysis-schedule">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-violet-700" /> Envoi automatique mensuel
                </p>
                <p className="text-xs text-gray-500">
                  Le {schedule.send_day} de chaque mois : rapport commercial PDF du mois écoulé
                  (graphiques inclus), envoyé aux destinataires ci-dessous.
                </p>
              </div>
              <Switch
                checked={!!schedule.enabled}
                onCheckedChange={(v) => saveSchedule({ enabled: v })}
                disabled={savingSchedule}
                data-testid="commercial-analysis-schedule-toggle"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
              <div className="min-w-0">
                <Label className="text-xs">Destinataires (séparés par des virgules)</Label>
                <Input
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  placeholder="direction@baobab.ci, commercial@baobab.ci"
                  className="h-9 text-sm"
                  data-testid="commercial-analysis-recipients"
                />
              </div>
              <div>
                <Label className="text-xs">Jour</Label>
                <Input
                  type="number" min={1} max={28} value={schedule.send_day}
                  onChange={(e) => setSchedule({ ...schedule, send_day: Number(e.target.value) })}
                  className="h-9 text-sm w-20"
                  data-testid="commercial-analysis-send-day"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  variant="outline" onClick={() => saveSchedule({})} disabled={savingSchedule}
                  data-testid="commercial-analysis-save-schedule"
                >
                  {savingSchedule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enregistrer
                </Button>
                <Button
                  variant="outline" onClick={handleSendNow} disabled={sendingNow}
                  data-testid="commercial-analysis-send-now"
                >
                  {sendingNow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Envoyer maintenant
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommercialAiAnalysisCard;
